import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../components/AuthBridge';
import { 
    History, ChevronRight, Search, Calendar, Clock, Target, 
    ShieldCheck, Zap, ChevronLeft, Info, Loader2, 
    ArrowRight, ArrowLeft, Award, TrendingUp, BarChart2, 
    Sparkles, Filter, AlertTriangle, BookOpen, Brain, 
    CheckCircle2, Printer, Check, Copy
} from 'lucide-react';
import { 
    ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import { ModuleHeader } from '../components/ModuleHeader';
import { PageLoader } from '../components/PageLoader';
import { AdUnit } from '../components/AdUnit';
import { useThemeTokens } from '../hooks/useActiveTheme';

export const ExamHistory: React.FC = () => {
    const { user, activeCert, isLoading: authLoading } = useAuth();
    const tokens = useThemeTokens();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAudit, setSelectedAudit] = useState<any>(null);
    const [filter, setFilter] = useState<'all' | 'passed' | 'failed'>('all');
    const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'score-desc' | 'score-asc'>('date-desc');
    const [isHydrated, setIsHydrated] = useState(false);
    
    // Pagination State
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [copiedPromptId, setCopiedPromptId] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsHydrated(true), 150);
        return () => clearTimeout(timer);
    }, []);

    // Reset page 1 when filter, search or sorting constraints shift
    useEffect(() => {
        setPage(1);
    }, [searchTerm, filter, sortBy, limit, activeCert?.id]);

    const activeHistory = useMemo(() => {
        return (user?.history || []).filter(h => h.certId === activeCert?.id);
    }, [user?.history, activeCert?.id]);

    // Derived Statistics
    const stats = useMemo(() => {
        const items = activeHistory;
        const total = items.length;
        if (total === 0) return { avgScore: 0, successRate: 0, weakCategory: 'N/A' };
        
        const passedCount = items.filter(h => h.passed).length;
        const sumScores = items.reduce((acc, current) => acc + (current.score || 0), 0);
        const avgScore = Math.round(sumScores / total);
        const successRate = Math.round((passedCount / total) * 100);

        // Aggregate weak category calculations
        const scoresByCategory: Record<string, { sum: number, count: number }> = {};
        items.forEach(h => {
            const domainData = h.categoryBreakdown || h.masteryByDomain || [];
            domainData.forEach((domain: any) => {
                const name = domain.name || 'General';
                const score = domain.score ?? 0;
                if (!scoresByCategory[name]) {
                    scoresByCategory[name] = { sum: 0, count: 0 };
                }
                scoresByCategory[name].sum += score;
                scoresByCategory[name].count += 1;
            });
        });

        let weakCategory = 'N/A';
        let lowestAvg = 100;
        Object.entries(scoresByCategory).forEach(([category, data]) => {
            const avg = data.sum / data.count;
            if (avg < lowestAvg) {
                lowestAvg = avg;
                weakCategory = category;
            }
        });

        return { avgScore, successRate, weakCategory };
    }, [activeHistory]);

    // Filtered Content
    const filteredHistory = useMemo(() => {
        let items = [...activeHistory];
        
        if (filter === 'passed') items = items.filter(h => h.passed);
        if (filter === 'failed') items = items.filter(h => !h.passed);
        
        if (searchTerm) {
            const cleanSearch = searchTerm.toLowerCase();
            items = items.filter(h => 
                h.stateCode?.toLowerCase().includes(cleanSearch) ||
                (h.examMode || 'Exam').toLowerCase().includes(cleanSearch) ||
                h.date?.includes(searchTerm)
            );
        }

        // Sorting
        items.sort((a, b) => {
            if (sortBy === 'date-desc') return new Date(b.date || '').getTime() - new Date(a.date || '').getTime();
            if (sortBy === 'date-asc') return new Date(a.date || '').getTime() - new Date(b.date || '').getTime();
            if (sortBy === 'score-desc') return (b.score || 0) - (a.score || 0);
            if (sortBy === 'score-asc') return (a.score || 0) - (b.score || 0);
            return 0;
        });
        
        return items;
    }, [activeHistory, filter, searchTerm, sortBy]);

    const paginatedHistory = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredHistory.slice(start, start + limit);
    }, [filteredHistory, page, limit]);

    const totalPages = Math.ceil(filteredHistory.length / limit);

    const handlePrint = () => {
        window.print();
    };

    const copyTutorPrompt = (weakDomain: string) => {
        const targetPrompt = `I just took a CNA practice simulation. I need study help regarding my lowest scoring area: "${weakDomain}". Can you explain the primary clinical criteria, standard steps, and auto-fail safety traps I must master for this topic?`;
        navigator.clipboard.writeText(targetPrompt).then(() => {
            setCopiedPromptId(true);
            setTimeout(() => setCopiedPromptId(false), 2000);
        });
    };

    if (authLoading) {
        return <PageLoader message="Retrieving Registry Telemetry..." />;
    }

    // Selected Detailed Audit Screen
    if (selectedAudit) {
        const domainData = selectedAudit.categoryBreakdown || selectedAudit.masteryByDomain || [];
        
        // Group domains by secure (>=75%) versus prioritize review (<75%)
        const secureDomains = domainData.filter((d: any) => d.score >= 75);
        const weakDomains = domainData.filter((d: any) => d.score < 75);

        // Find weakest domain in this single test run to build personalized prompts
        let absoluteWeakest = 'Clinical Safety Standards';
        if (domainData.length > 0) {
            const sortedByScore = [...domainData].sort((a: any, b: any) => (a.score ?? 0) - (b.score ?? 0));
            absoluteWeakest = sortedByScore[0]?.name || absoluteWeakest;
        }

        return (
            <div className="max-w-5xl mx-auto py-4 animate-fade-in text-slate-900 dark:text-slate-100 pb-24">
                <button 
                    onClick={() => setSelectedAudit(null)}
                    className="mb-6 flex items-center gap-2 text-[10px] font-black tracking-widest text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors uppercase select-none"
                >
                    <ChevronLeft size={16} /> Back to Session Log
                </button>

                {/* Score Hero Accolade Header card */}
                <div className={`rounded-[2.5rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden mb-8 ${selectedAudit.passed ? 'bg-gradient-to-r from-emerald-600 via-teal-655 to-emerald-700' : 'bg-gradient-to-r from-rose-600 via-rose-700 to-slate-900'}`}>
                    <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 blur-[90px] rounded-full"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div>
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <div className="px-3 py-1 bg-white/15 text-[9px] font-black uppercase tracking-widest rounded-full border border-white/20 flex items-center gap-1.5 backdrop-blur-md">
                                    <ShieldCheck size={12} className={selectedAudit.passed ? 'text-emerald-300' : 'text-rose-300'} />
                                    <span>Board Standard Run</span>
                                </div>
                                <span className="text-[10px] font-semibold text-white/80">Protocol Integrity: Verified</span>
                            </div>
                            
                            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 leading-none">
                                {selectedAudit.passed ? 'Registry Qualified 🎉' : 'Remediation Required ⚠️'}
                            </h2>
                            
                            <p className="text-white/85 font-semibold text-sm leading-relaxed max-w-xl">
                                Completed on <span className="underline decoration-white/30 font-black">{selectedAudit.date}</span> styled for <span className="underline decoration-white/30 font-black">{selectedAudit.stateCode || 'Global'}</span> state board standards. This simulation accurately replicates official clinical test guidelines.
                            </p>
                            
                            <div className="mt-8 flex flex-wrap gap-3">
                                <button 
                                    onClick={handlePrint}
                                    className="px-5 py-3 bg-white hover:bg-slate-50 text-slate-900 font-extrabold text-[9.5px] uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center gap-2 active:scale-95 cursor-pointer"
                                >
                                    <Printer size={13} /> Save PDF Report
                                </button>
                                <button 
                                    onClick={() => copyTutorPrompt(absoluteWeakest)}
                                    className="px-5 py-3 bg-slate-950/40 hover:bg-slate-950/60 text-white font-black text-[9.5px] uppercase tracking-widest rounded-xl transition-all border border-white/10 flex items-center gap-2 active:scale-95 cursor-pointer"
                                >
                                    {copiedPromptId ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                                    <span>Sync to AI Tutor</span>
                                </button>
                            </div>
                        </div>

                        {/* Large Recharts custom indicator ring */}
                        <div className="w-36 h-36 md:w-44 md:h-44 relative flex items-center justify-center overflow-hidden shrink-0">
                             {isHydrated ? (
                               <div className="w-full h-full relative z-10 scale-105">
                                 <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie 
                                            data={[{ value: selectedAudit.score }, { value: 100 - selectedAudit.score }]} 
                                            innerRadius={55} 
                                            outerRadius={75} 
                                            startAngle={90} 
                                            endAngle={450} 
                                            dataKey="value" 
                                            stroke="none"
                                            cornerRadius={12}
                                        >
                                            <Cell fill="#ffffff" />
                                            <Cell fill={selectedAudit.passed ? 'rgba(16,185,129,0.35)' : 'rgba(244,63,94,0.35)'} />
                                        </Pie>
                                    </PieChart>
                                 </ResponsiveContainer>
                               </div>
                             ) : <Loader2 className="animate-spin text-white" />}
                             <div className="absolute flex flex-col items-center">
                                <span className="text-3xl md:text-4xl font-black text-white leading-none">{selectedAudit.score}%</span>
                                <span className="text-[7.5px] font-black tracking-widest text-white/80 uppercase mt-1.5">Accuracy</span>
                             </div>
                        </div>
                    </div>
                </div>

                {/* Performance split Grid columns layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Diagnostic Proficiency breakdown panel */}
                    <div className="lg:col-span-7 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl space-y-8">
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                            <div>
                                <h3 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">Domain Diagnostics</h3>
                                <p className="text-[10px] text-slate-400 font-black tracking-wider uppercase mt-1">Accredited Subject performance</p>
                            </div>
                            <BarChart2 className="text-slate-400 dark:text-slate-500 shrink-0" size={24} />
                        </div>

                        {/* Weak Review Domains (<75%) */}
                        {weakDomains.length > 0 && (
                            <div className="space-y-4">
                                <h4 className="text-[9px] font-black tracking-widest text-pink-600 dark:text-pink-400 uppercase flex items-center gap-1.5 p-1.5 bg-pink-50 dark:bg-pink-950/20 rounded-lg w-fit">
                                    <AlertTriangle size={12} /> Primary Remediation priorities (Below 75%)
                                </h4>
                                <div className="space-y-3.5">
                                    {weakDomains.map((domain: any) => (
                                        <div key={domain.name} className="space-y-2 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-150 dark:border-slate-800 group hover:border-pink-300 dark:hover:border-pink-900 transition-all">
                                            <div className="flex justify-between items-center text-xs">
                                                <p className="font-black text-slate-700 dark:text-slate-350">{domain.name}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 bg-pink-100 dark:bg-pink-955/40 text-pink-700 dark:text-pink-400 font-black text-[8px] rounded uppercase tracking-wider">Remedial</span>
                                                    <span className="font-extrabold text-slate-950 dark:text-white">{domain.score}%</span>
                                                </div>
                                            </div>
                                            <div className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-pink-500 rounded-full transition-all duration-1000" style={{ width: `${domain.score}%` }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Secure Mastery Domains (>=75%) */}
                        {secureDomains.length > 0 && (
                            <div className="space-y-4">
                                <h4 className="text-[9px] font-black tracking-widest text-emerald-600 dark:text-emerald-400 uppercase flex items-center gap-1.5 p-1.5 bg-emerald-50 dark:bg-emerald-955/20 rounded-lg w-fit">
                                    <CheckCircle2 size={12} /> Secure Mastery Thresholds (75% or Superior)
                                </h4>
                                <div className="space-y-3.5">
                                    {secureDomains.map((domain: any) => (
                                        <div key={domain.name} className="space-y-2 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800 group hover:border-emerald-300 dark:hover:border-emerald-900 transition-all">
                                            <div className="flex justify-between items-center text-xs">
                                                <p className="font-black text-slate-700 dark:text-slate-350">{domain.name}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 bg-emerald-105/90 dark:bg-emerald-955/40 text-emerald-700 dark:text-emerald-400 font-black text-[8px] rounded uppercase tracking-wider">Passed</span>
                                                    <span className="font-extrabold text-slate-950 dark:text-white">{domain.score}%</span>
                                                </div>
                                            </div>
                                            <div className="h-2 w-full bg-slate-200 dark:bg-slate-850 rounded-full overflow-hidden">
                                                <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${domain.score}%` }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {domainData.length === 0 && (
                            <div className="py-12 text-center opacity-45 flex flex-col items-center justify-center border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-2xl">
                                <Info size={36} className="text-slate-300 mb-2" />
                                <p className="text-xs font-black uppercase tracking-wider text-slate-500">Category breakdown data is unlogged for this run.</p>
                            </div>
                        )}
                    </div>

                    {/* Bento parameter grid with speed, stats and links */}
                    <div className="lg:col-span-5 space-y-6">
                        <AdUnit placement="history_details" />

                        {/* Metrics Bento Blocks */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col justify-between h-36 relative overflow-hidden group hover:border-indigo-400 transition-all">
                                <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-xl w-fit">
                                    <Zap size={18} />
                                </div>
                                <div>
                                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-2">Core Pace Rate</h4>
                                    <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{selectedAudit.avgTimePerQuestion || '12'}s <span className="text-xs font-bold text-slate-400 tracking-wider">/ Item</span></p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col justify-between h-36 relative overflow-hidden group hover:border-teal-400 transition-all">
                                <div className="p-3 bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 rounded-xl w-fit">
                                    <Clock size={18} />
                                </div>
                                <div>
                                    <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-2">Active Run Time</h4>
                                    <p className="text-2xl font-black text-slate-900 dark:text-white mt-1 truncate">{selectedAudit.totalTimeFormatted || '14:20'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Practical Action Study plan guide */}
                        <div className={`${tokens.heroBannerBg} rounded-[2rem] p-6 text-white space-y-4 relative overflow-hidden shadow-2xl border border-white/10`}>
                            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl"></div>
                            <div className={`flex items-center gap-2.5 ${tokens.accentText} border-b border-white/10 pb-3`}>
                                <Brain size={20} className="animate-pulse" />
                                <span className="text-[10px] font-black tracking-widest uppercase">Adaptive Rationale Diagnostic</span>
                            </div>
                            <p className="text-xs font-bold text-slate-300 leading-relaxed">
                                {selectedAudit.passed 
                                    ? "Candidate performance passes standard registry bounds safely. Maintain proficiency checks weekly to secure skills retention."
                                    : `This audit flagged ${selectedAudit.incorrectCount || 'critical'} errors. Standard safety criteria require a 100% mastery of auto-fail safety rules (like hands washing protocols and wheelchair locking limits) prior to board registry admittance.`
                                }
                            </p>
                            
                            {/* Copy-paste prompt tutorial support */}
                            <div className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-1.5 text-[10px]">
                                <span className={`font-black ${tokens.accentText} tracking-wide block uppercase text-[8.5px]`}>💡 AI Tutor Custom Prompt</span>
                                <p className="text-slate-400 font-medium leading-normal italic">
                                    "I scored lowest in {absoluteWeakest}, help me master..."
                                </p>
                                <button
                                    onClick={() => copyTutorPrompt(absoluteWeakest)}
                                    className="pt-1.5 font-black text-white hover:text-indigo-300 transition-colors uppercase outline-none text-[8px] flex items-center gap-1 select-none cursor-pointer"
                                >
                                    {copiedPromptId ? 'Copied Prompt to Clipboard!' : 'Copy Prompt & Launch Tutor'}
                                </button>
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        );
    }

    // Default History list main Catalog Workspace screen
    return (
        <div className="max-w-6xl mx-auto pb-24 text-slate-900 dark:text-slate-100 animate-fade-in">
            
            <ModuleHeader 
                icon={History}
                title="My Past Scores"
                description="Look at your past scores and see how you did on previous practice tests."
                badge="Past Tests"
                color="indigo"
            />

            {/* Top Row: Stunning Interactive Dashboard Analytics Board */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                
                {/* Score average dial progress ring */}
                <div className={`bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-xl flex items-center justify-between gap-4 relative overflow-hidden group ${tokens.cardHoverBorder} transition-all select-none`}>
                    <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-xl"></div>
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <TrendingUp className={`${tokens.accentText} hover:scale-115 transition-transform shrink-0`} size={16} />
                            <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-400">Readiness Index</h3>
                        </div>
                        <p className="text-3xl font-black text-slate-900 dark:text-white mt-1">{stats.avgScore}%</p>
                        <p className="text-[9px] text-slate-400 dark:text-slate-500 font-extrabold uppercase mt-0.5">Average Accuracy Score</p>
                    </div>

                    {/* SVG Radial Core circle */}
                    <div className="w-[72px] h-[72px] shrink-0 relative flex items-center justify-center">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle cx="36" cy="36" r="30" className="stroke-slate-100 dark:stroke-slate-800 fill-transparent" strokeWidth="6" />
                            <circle 
                                cx="36" cy="36" r="30" 
                                className={`${tokens.accentSvgStroke || 'stroke-indigo-600'} fill-transparent transition-all duration-1000`} 
                                strokeWidth="6" 
                                strokeDasharray={188.4}
                                strokeDashoffset={188.4 - (188.4 * stats.avgScore) / 100}
                                strokeLinecap="round"
                            />
                        </svg>
                        <span className="absolute text-[11px] font-black">{stats.avgScore}%</span>
                    </div>
                </div>

                {/* Accuracy/Success Rate tracker */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-xl gap-4 select-none relative overflow-hidden group hover:border-emerald-400 transition-all">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-xl"></div>
                    <div className="flex justify-between items-start">
                        <div className="space-y-1.5 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <Award className="text-emerald-500 shrink-0" size={16} />
                                <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-400">Success Ratio</h3>
                            </div>
                            <p className="text-3xl font-black text-emerald-555 dark:text-emerald-400 mt-1">{stats.successRate}%</p>
                            <p className="text-[9px] text-slate-400 dark:text-slate-500 font-extrabold uppercase mt-0.5">{activeHistory.filter(h => h.passed).length} of {activeHistory.length} Runs Passed</p>
                        </div>
                        <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/25 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-md font-black uppercase text-[7px] tracking-wider shrink-0 mt-0.5">Active</span>
                    </div>
                </div>

                {/* Remediation Weakest Concept pointer */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden group hover:border-pink-400 transition-all select-none col-span-1">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/10 rounded-full blur-xl"></div>
                    <div className="space-y-1.5 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <Brain className="text-pink-500 shrink-0" size={16} />
                            <h3 className="text-[10px] uppercase tracking-widest font-black text-slate-450">Remedial Focus</h3>
                        </div>
                        <p className="text-sm font-black text-slate-900 dark:text-white mt-2 truncate line-clamp-1 max-w-[220px]" title={stats.weakCategory}>{stats.weakCategory}</p>
                        <p className="text-[9px] text-slate-450 dark:text-slate-500 font-extrabold uppercase mt-1">Recommended next training</p>
                    </div>
                </div>

            </div>

            <div className="mt-8 space-y-6">
                <AdUnit placement="history_list" />
                
                {/* Advanced control bar container: search, filter & sort dropdowns */}
                <div className="sticky top-0 z-20 flex flex-col md:flex-row gap-4 items-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-md p-4 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-md">
                    
                    {/* Search in history registry */}
                    <div className="relative flex-1 w-full group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Search logs by dates, state code rules, or test agency..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-850 rounded-xl outline-none focus:bg-white dark:focus:bg-slate-950 transition-all font-bold text-xs"
                        />
                    </div>
                    
                    {/* Filter and Sorting triggers */}
                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto self-stretch md:self-auto shrink-0">
                        
                        {/* Status filter segment tabs */}
                        <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-xl shadow-inner divide-x divide-slate-200/50 dark:divide-slate-800">
                            {[
                                { id: 'all', text: 'All Runs' }, 
                                { id: 'passed', text: 'Passed' }, 
                                { id: 'failed', text: 'Remediation' }
                            ].map(tab => (
                                <button 
                                    key={tab.id} 
                                    onClick={() => setFilter(tab.id as any)}
                                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${filter === tab.id ? tokens.activeTab : tokens.inactiveTab}`}
                                >
                                    {tab.text}
                                </button>
                            ))}
                        </div>

                        {/* Sorting actions options */}
                        <div className="relative flex items-center bg-slate-50 dark:bg-slate-950 px-3 py-2 border border-slate-100 dark:border-slate-850 rounded-xl">
                            <Filter size={11} className="text-slate-400 mr-1.5" />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className="bg-transparent text-[9px] font-black uppercase tracking-wider outline-none text-slate-650 cursor-pointer"
                            >
                                <option value="date-desc">Newest Date</option>
                                <option value="date-asc">Oldest Date</option>
                                <option value="score-desc">Highest Score</option>
                                <option value="score-asc">Lowest Score</option>
                            </select>
                        </div>
                    </div>

                </div>

                {/* History list card catalog cards grid deck */}
                <div className="space-y-4">
                    {paginatedHistory.length === 0 ? (
                        <div className="bg-white dark:bg-slate-900 rounded-[2.2rem] p-16 text-center border-4 border-dashed border-slate-100 dark:border-slate-850 flex flex-col items-center gap-5 justify-center">
                            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-full border border-slate-100 dark:border-slate-850">
                                <History size={48} className="text-slate-300 dark:text-slate-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-wider">Historical Logs Vacant</h3>
                                <p className="text-[10px] text-slate-450 dark:text-slate-500 font-bold max-w-xs mt-1 leading-normal">
                                    {filteredHistory.length !== activeHistory.length 
                                        ? "No logs match your filters. Attempt adjusting sorting constraints." 
                                        : `No recorded exam history found for the ${activeCert?.shortName || ''} cert path. Complete practice simulations to log results.`}
                                </p>
                            </div>
                        </div>
                    ) : (
                        paginatedHistory.map((audit) => {
                            const isRunPassed = audit.passed;
                            return (
                                <button 
                                    key={audit.id}
                                    onClick={() => setSelectedAudit(audit)}
                                    style={{ contentVisibility: 'auto' }}
                                    className="w-full bg-white dark:bg-slate-900 p-5 rounded-[2.2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between gap-4 hover:shadow-lg dark:hover:bg-slate-850/30 hover:border-indigo-400 dark:hover:border-indigo-950 transition-all duration-300 group text-left active:scale-[0.99]"
                                >
                                    <div className="flex items-center gap-5 min-w-0">
                                        
                                        {/* Left Score metric indicator pie */}
                                        <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center font-black shrink-0 shadow-sm relative overflow-hidden transition-all duration-300 group-hover:scale-105 border ${isRunPassed ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-950' : 'bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-450 border-rose-100 dark:border-rose-950'}`}>
                                            <span className="text-base leading-none">{audit.score}%</span>
                                            <span className="text-[6px] tracking-widest uppercase font-black mt-1">{isRunPassed ? 'PASS' : 'FAIL'}</span>
                                        </div>

                                        {/* Center Detail Logs */}
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="px-1.5 py-0.5 bg-slate-900 dark:bg-slate-950 text-white text-[7px] font-black rounded uppercase tracking-widest">{audit.stateCode || 'Global'}</span>
                                                <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-widest">{audit.examMode || 'Simulation'}</span>
                                            </div>
                                            
                                            <h3 className="text-xs md:text-sm font-black text-slate-800 dark:text-slate-100 truncate flex items-center gap-1.5 leading-none">
                                                Clinical Readiness Assessment
                                                {isRunPassed ? (
                                                  <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                                                ) : <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
                                            </h3>

                                            <div className="flex items-center gap-4 mt-2 font-black text-slate-400 text-[8.5px] uppercase tracking-wider">
                                                <div className="flex items-center gap-1">
                                                    <Calendar size={11} className="text-slate-350 shrink-0" /> 
                                                    <span>{audit.date}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Clock size={11} className="text-slate-350 shrink-0" /> 
                                                    <span>{audit.totalTimeFormatted || 'Paced'}</span>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Action Hover element wrapper */}
                                    <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-300 dark:text-slate-700 group-hover:bg-indigo-600 group-hover:text-white transition-all shrink-0">
                                        <ChevronRight size={18} />
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-850 p-4 flex items-center justify-between shadow-md">
                        <button 
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-300 text-white font-black text-[9px] uppercase tracking-widest disabled:opacity-20 transition-all active:scale-95"
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                        
                        <div className="flex flex-col items-center select-none">
                            <span className="text-[7px] font-black text-slate-400 tracking-widest uppercase mb-1">Index Page</span>
                            <div className="px-3.5 py-1.5 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white rounded-lg font-black text-xs border border-slate-100 dark:border-slate-850">
                                {page} / {totalPages}
                            </div>
                        </div>

                        <button 
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-300 text-white font-black text-[9px] uppercase tracking-widest disabled:opacity-20 transition-all active:scale-95"
                        >
                            Next <ArrowRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
